// Appairage d'un écran gérant : l'écran affiche un code court (ex 929-GHK),
// on le saisit ici en choisissant l'établissement cible (cas multi-sites).
// Le back-end émet alors une clé sk_live scopée au site et l'écran bascule
// sur ses tuiles en quelques secondes. Endpoints : /api/v1/screen-manager/pairing/*
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AddToQueue, LinkOff } from "@mui/icons-material";
import { postData } from "../API/Post";
import fetchData from "../API/Fetch";
import { deleteData } from "../API/Delete";

const CLAIM_ERRORS = {
  404: "Code inconnu — vérifiez la saisie sur l'écran.",
  409: "Ce code a déjà été utilisé. Régénérez un code sur l'écran (menu Réglages → Appairage).",
  410: "Code expiré — l'écran en affiche un nouveau automatiquement.",
  403: "Vous n'avez pas les droits sur cet établissement.",
};

function PairScreen() {
  const establishments = useSelector((state) => state.establishments) || [];

  const [code, setCode] = useState("");
  const [establishmentId, setEstablishmentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {severity, message}
  const [screens, setScreens] = useState(null);

  // Un seul site : pas de choix à faire (le cas multi-sites affiche la liste)
  useEffect(() => {
    if (establishments.length === 1) setEstablishmentId(establishments[0].id);
  }, [establishments]);

  const loadScreens = async (estId) => {
    if (!estId) return;
    const data = await fetchData(
      `/api/v1/screen-manager/pairing/screens?establishment_id=${estId}`
    );
    setScreens(data ? data.screens : null);
  };

  useEffect(() => {
    loadScreens(establishmentId);
  }, [establishmentId]);

  const handleClaim = async () => {
    setResult(null);
    if (!code.trim()) {
      setResult({ severity: "warning", message: "Saisissez le code affiché sur l'écran." });
      return;
    }
    if (!establishmentId) {
      setResult({ severity: "warning", message: "Choisissez l'établissement de cet écran." });
      return;
    }
    setBusy(true);
    const response = await postData("/api/v1/screen-manager/pairing/claim", {
      code: code.trim(),
      establishment_id: establishmentId,
    });
    setBusy(false);

    if (response && response.status === 200 && response.data && response.data.ok) {
      setResult({
        severity: "success",
        message: `Écran appairé à « ${response.data.establishment.name} » — il affiche les données du site d'ici quelques secondes.`,
      });
      setCode("");
      loadScreens(establishmentId);
      return;
    }
    const status = response && response.response ? response.response.status : null;
    setResult({
      severity: "error",
      message: CLAIM_ERRORS[status] || "Erreur inattendue — réessayez.",
    });
  };

  const handleDetach = async (screen) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Détacher l'écran ${screen.deveui} ?\n\nSa clé sera révoquée : il repassera en mode appairage (nouveau code affiché).`
    );
    if (!ok) return;
    await deleteData(`/api/v1/screen-manager/pairing/screens/${screen.pairing_id}`);
    loadScreens(establishmentId);
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString("fr-FR") : "—";

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddToQueue /> Appairer un écran
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          L'écran affiche un code du type <b>929-GHK</b> tant qu'il n'est associé à
          aucun site. Saisissez ce code et choisissez l'établissement : l'écran
          bascule sur les données du site en quelques secondes.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Code affiché sur l'écran"
            placeholder="929-GHK"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            inputProps={{ style: { letterSpacing: 3, fontWeight: 600 } }}
            autoFocus
          />
          <TextField
            select
            label="Établissement"
            value={establishmentId}
            onChange={(e) => setEstablishmentId(e.target.value)}
            disabled={establishments.length === 1}
            helperText={
              establishments.length > 1
                ? "Cet écran affichera UNIQUEMENT les installations de ce site."
                : ""
            }
          >
            {establishments.map((est) => (
              <MenuItem key={est.id} value={est.id}>
                {est.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={handleClaim}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={18} /> : <AddToQueue />}
          >
            {busy ? "Appairage..." : "Appairer"}
          </Button>
          {result && <Alert severity={result.severity}>{result.message}</Alert>}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Écrans appairés
            {establishments.length > 1 && establishmentId
              ? ` — ${
                  (establishments.find((e) => e.id === establishmentId) || {}).name || ""
                }`
              : ""}
          </Typography>
          {!establishmentId && (
            <Typography variant="body2" color="text.secondary">
              Choisissez un établissement pour voir ses écrans.
            </Typography>
          )}
          {establishmentId && screens && screens.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Aucun écran appairé sur ce site.
            </Typography>
          )}
          {establishmentId && screens && screens.length > 0 && (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Identifiant (DevEUI)</TableCell>
                    <TableCell>Appairé le</TableCell>
                    <TableCell>Dernier contact</TableCell>
                    <TableCell>Clé</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {screens.map((screen) => (
                    <TableRow key={screen.pairing_id}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{screen.deveui}</TableCell>
                      <TableCell>{formatDate(screen.paired_at)}</TableCell>
                      <TableCell>{formatDate(screen.last_seen_at)}</TableCell>
                      <TableCell>{screen.key_active ? "Active" : "Révoquée"}</TableCell>
                      <TableCell align="right">
                        {screen.key_active && (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<LinkOff />}
                            onClick={() => handleDetach(screen)}
                          >
                            Détacher
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(PairScreen);
